import { exportToCSV, exportToExcel, exportToPDF } from '@/lib/formatters'
import { DataTableColumn } from '@/components/common/DataTable'

/**
 * Format value for export - rounds numbers to integers, handles undefined/null
 * Note: All numbers are rounded to whole numbers for cleaner presentation in salary reports
 */
function formatValueForExport(value: any, preserveNumbers = false): any {
  if (value === undefined || value === null) {
    return ''
  }

  if (typeof value === 'number' && !isNaN(value)) {
    return preserveNumbers ? Math.round(value) : Math.round(value).toString()
  }

  return value
}

/**
 * Prepare data for export by filtering columns and formatting values
 * Common logic used by CSV, Excel, and PDF export functions
 */
function prepareDataForExport<T>(
  data: T[],
  columns: DataTableColumn<T>[],
  preserveNumbers = false
): { headers: string[]; exportData: Record<string, any>[] } {
  // Filter out technical columns that shouldn't be exported (actions, internal columns)
  const exportableColumns = columns.filter(
    col => col.key !== 'actions' && !String(col.key).startsWith('_')
  )

  // Extract headers and data based on columns
  const headers = exportableColumns.map(col => col.header)
  const exportData = data.map(row => {
    const rowData: Record<string, any> = {}
    exportableColumns.forEach(col => {
      // Use exportValue if defined, otherwise use raw value from row
      const value = col.exportValue ? col.exportValue(row) : row[col.key]
      // Format value for export (handles numbers, undefined, null)
      rowData[col.header] = formatValueForExport(value, preserveNumbers)
    })
    return rowData
  })

  return { headers, exportData }
}

/**
 * Generic sorting function for report data
 */
export function sortReportData<T>(
  data: T[],
  key: keyof T | null,
  direction: 'asc' | 'desc' | null
): T[] {
  if (!key || !direction) return data

  return [...data].sort((a, b) => {
    const aValue = a[key]
    const bValue = b[key]

    if (aValue === null || aValue === undefined) return 1
    if (bValue === null || bValue === undefined) return -1

    let comparison = 0
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      comparison = aValue.localeCompare(bValue)
    } else if (typeof aValue === 'number' && typeof bValue === 'number') {
      comparison = aValue - bValue
    } else if (aValue instanceof Date && bValue instanceof Date) {
      comparison = aValue.getTime() - bValue.getTime()
    } else {
      comparison = String(aValue).localeCompare(String(bValue))
    }

    return direction === 'asc' ? comparison : -comparison
  })
}

/**
 * Generic filtering function for report data
 */
export function filterReportData<T>(
  data: T[],
  columns: DataTableColumn<T>[],
  searchValue: string
): T[] {
  if (!searchValue) return data

  return data.filter((row) =>
    columns.some((column) => {
      const value = row[column.key]
      return value?.toString().toLowerCase().includes(searchValue.toLowerCase())
    })
  )
}

/**
 * Export report data to CSV
 * @param data - Array of data to export
 * @param columns - Column definitions (should already be filtered by visibility)
 * @param filename - Output filename
 */
export function exportReportToCSV<T>(
  data: T[],
  columns: DataTableColumn<T>[],
  filename: string = 'report.csv'
) {
  const { headers, exportData } = prepareDataForExport(data, columns)
  exportToCSV(exportData, filename, headers)
}

/**
 * Export report data to Excel
 * @param data - Array of data to export
 * @param columns - Column definitions (should already be filtered by visibility)
 * @param filename - Output filename
 */
export async function exportReportToExcel<T>(
  data: T[],
  columns: DataTableColumn<T>[],
  filename: string = 'report.xlsx'
) {
  const { headers, exportData } = prepareDataForExport(data, columns, true)
  await exportToExcel(exportData, filename, headers)
}

/**
 * Export report data to PDF using jsPDF autoTable
 */
export async function exportReportToPDF<T>(
  data: T[],
  columns: DataTableColumn<T>[],
  filename: string = 'report.pdf'
) {
  const { headers, exportData } = prepareDataForExport(data, columns)
  await exportToPDF(exportData, filename, headers)
}

/**
 * Create a standard export handler that prompts user for format choice
 */
export function createReportExportHandler<T>(
  getData: () => T[],
  columns: DataTableColumn<T>[],
  baseFilename: string
) {
  return async () => {
    const data = getData()
    if (data.length === 0) {
      alert('Inga data att exportera')
      return
    }

    const format = window.confirm('Välj format:\nOK = Excel (.xlsx)\nAvbryt = CSV (.csv)')

    const timestamp = new Date().toISOString().split('T')[0] // YYYY-MM-DD
    const filename = `${baseFilename}_${timestamp}`

    if (format) {
      await exportReportToExcel(data, columns, `${filename}.xlsx`)
    } else {
      exportReportToCSV(data, columns, `${filename}.csv`)
    }
  }
}

/**
 * Calculate basic summary statistics for numeric columns
 */
export function calculateNumericSummary<T>(
  data: T[],
  key: keyof T
): {
  total: number
  average: number
  min: number
  max: number
  count: number
} {
  const values = data
    .map(item => Number(item[key]))
    .filter(val => !isNaN(val))

  if (values.length === 0) {
    return { total: 0, average: 0, min: 0, max: 0, count: 0 }
  }

  const total = values.reduce((sum, val) => sum + val, 0)
  const average = total / values.length
  const min = Math.min(...values)
  const max = Math.max(...values)

  return {
    total,
    average: Math.round(average * 100) / 100, // Round to 2 decimal places
    min,
    max,
    count: values.length
  }
}

/**
 * Count unique values in a column
 */
export function countUniqueValues<T>(data: T[], key: keyof T): number {
  const uniqueValues = new Set(data.map(item => item[key]))
  return uniqueValues.size
}

/**
 * Group data by a specific column value
 */
export function groupDataBy<T>(
  data: T[],
  key: keyof T
): Record<string, T[]> {
  return data.reduce((groups, item) => {
    const groupKey = String(item[key])
    if (!groups[groupKey]) {
      groups[groupKey] = []
    }
    groups[groupKey].push(item)
    return groups
  }, {} as Record<string, T[]>)
}